// Core Web Vitals -> Google Analytics 4.
// Reports LCP, CLS, INP, FCP and TTFB as GA4 events so they show up under
// Reports > Engagement > Events (event name "web_vitals") and can be turned
// into a custom report. No extra library: uses the browser's PerformanceObserver.

const send = (name, value, rating, extra = {}) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'web_vitals', {
    metric_name: name,
    metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
    metric_rating: rating,
    non_interaction: true,
    ...extra,
  });
};

const rate = (name, v) => {
  const t = { LCP: [2500, 4000], CLS: [0.1, 0.25], INP: [200, 500], FCP: [1800, 3000], TTFB: [800, 1800] }[name];
  return v <= t[0] ? 'good' : v <= t[1] ? 'needs-improvement' : 'poor';
};

const observe = (type, cb, opts = {}) => {
  try {
    const po = new PerformanceObserver((list) => cb(list.getEntries()));
    po.observe({ type, buffered: true, ...opts });
    return po;
  } catch {
    return null;
  }
};

export function reportWebVitals() {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;
  const sent = new Set();
  const once = (name, value, extra) => {
    if (sent.has(name)) return;
    sent.add(name);
    send(name, value, rate(name, value), extra);
  };

  // TTFB + FCP
  const nav = performance.getEntriesByType('navigation')[0];
  if (nav) once('TTFB', nav.responseStart);
  observe('paint', (entries) => {
    const fcp = entries.find((e) => e.name === 'first-contentful-paint');
    if (fcp) once('FCP', fcp.startTime);
  });

  // LCP: final value is reported when the page is hidden or user interacts
  let lcp = 0;
  let lcpEl = '';
  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) { lcp = last.startTime; lcpEl = last.element?.tagName?.toLowerCase() || ''; }
  });

  // CLS: sum of session windows (5s max, 1s gap)
  let cls = 0; let session = 0; let sessionStart = 0; let lastShift = 0;
  observe('layout-shift', (entries) => {
    entries.forEach((e) => {
      if (e.hadRecentInput) return;
      if (e.startTime - lastShift > 1000 || e.startTime - sessionStart > 5000) { session = 0; sessionStart = e.startTime; }
      session += e.value; lastShift = e.startTime; cls = Math.max(cls, session);
    });
  });

  // INP: worst interaction (p98 approximation: 2nd worst after 50 interactions)
  const durations = [];
  observe('event', (entries) => {
    entries.forEach((e) => { if (e.interactionId) durations.push(e.duration); });
  }, { durationThreshold: 40 });

  const flush = () => {
    if (lcp) once('LCP', lcp, { lcp_element: lcpEl });
    once('CLS', cls);
    if (durations.length) {
      const sorted = [...durations].sort((a, b) => b - a);
      once('INP', sorted[Math.min(sorted.length - 1, Math.floor(durations.length / 50))]);
    }
  };
  const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('pagehide', flush);
  ['keydown', 'click', 'pointerdown'].forEach((t) => window.addEventListener(t, () => { if (lcp) once('LCP', lcp, { lcp_element: lcpEl }); }, { once: true, passive: true }));
}
